import { useAuth } from "@/contexts/AuthContext";
import { Landing } from "@/pages/Landing";
import { useLocation } from "react-router-dom";

interface RootGateProps {
  children: React.ReactNode;
}

/**
 * Acts as a smart gate for the root path:
 *  - If user is on "/" and NOT authenticated → render the public Landing page
 *  - If user is on "/" and authenticated → render the protected app (children)
 *  - For any other route under "/*" → enforce normal auth (redirect to /auth if needed)
 */
export const RootGate = ({ children }: RootGateProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isRoot = location.pathname === "/";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Public landing on "/" for guests
  if (isRoot && !user) {
    return <Landing />;
  }

  // Everything else requires auth — bounce back to landing (which shows login modal entry)
  if (!user) {
    window.location.replace("/");
    return null;
  }

  return <>{children}</>;
};
